import * as foreCasting from 'src/store/modules/runbooks/utils/foreCasting'
import * as uiOperations from 'src/store/modules/runbooks/utils/uiOperations'
import * as populateActionList from 'src/store/modules/runbooks/utils/populateActionList'
import * as actionFiltering from 'src/store/modules/runbooks/utils/actionFiltering'
import * as cmdbOperations from 'src/store/modules/runbooks/utils/cmdbOperations'
import * as validationOperations from 'src/store/modules/runbooks/utils/validationOperations'

export const initialActionState = {
    id: null, // this is the id from the database
    uid: null, // this is the id used for the UI
    startTime: null, // session start time
    endTime: null, // session end time
    potentialStartTime: null, // start time after estimation
    potentialEndTime: null, // end time after estimation
    name: '', // action name
    description: '', // action description (optional)
    customer: '', // customer name
    customerId: null, // customer org pk
    ownerUser: null,
    ownerUserId: null,
    ownerTeam: null,
    ownerTeamId: null,
    ownerSupplier: null,
    ownerSupplierId: null,
    cmdbOwnerTeam: null, // default owner used to be able to reset if freeze is not needed
    cmdbOwnerTeamId: null,
    freezeOwnerTeam: false, // gives the ability to keep the owner different than CMDB
    checkType: 'manual',
    children: [], // parallel children
    treeChildren: [], // all children, sequential and parallel
    parents: [], // actions on which this one depends in order to start execution
    treeParent: null, // first depth - 1 parent
    prevSibling: null,
    nextSibling: null,
    status: 0,
    device: '',
    deviceClass: '',
    environment: '',
    selected: false,
    reverse: true,
    autoSucceed: false,
    monitored: false,
    customUrl: '',
    color: '',
    expanded: false,
    estimatedDuration: 0,
    infoOnly: false,
    depth: 0,
    filteredByUser: false, // This is mostly used by the UI filterControls (eg: owned by me, owned by teams, etc...)
    filteredByAction: false, // This is used by DR solutions filters (eg: tier=Tier3, Locations='Hasselt', etc...)
    visible: false,
    isAvZone: false,
    markedForDeletion: false,
    doNotStartBeforeTime: null,
    gapTime: 0,
    filters: [],
}

export default class ActionsHelper {
    VALID_TREE_MESSAGE = 'valid action tree'
    CIRCULAR_TREE_MESSAGE =
        'The tree is circular, it will cause recursion errors.'

    actionTypes = {
        trigger: 'trigger',
        topsolution: 'topsolution',
        solution: 'solution',
        avzone: 'avzone',
        virp: 'virp',
        device: 'device',
        def: 'def',
    }

    constructor({
        actions: { byId, ciToUid, topAction, lastUid },
        ...runbook
    }) {
        this.actionsMap = byId
        this.lastUid = lastUid || 1
        this.topAction = topAction
        this.ciToUid = ciToUid
        this.runbook = runbook
    }

    // Forecasting / Estimating how long an action takes to finish
    substractTimeFromStartToMinutes = foreCasting.substractTimeFromStartToMinutes.bind(
        this
    )
    startEstimateRunbook = foreCasting.startEstimateRunbook.bind(this)
    estimateAction = foreCasting.estimateAction.bind(this)
    calculateGapTime = foreCasting.calculateGapTime.bind(this)
    setIndividualPotentialTimes = foreCasting.setIndividualPotentialTimes.bind(
        this
    )

    // UI operations
    uprootAction = uiOperations.uprootAction.bind(this)
    cleanupChildren = uiOperations.cleanupChildren.bind(this)
    plantAction = uiOperations.plantAction.bind(this)
    moveAction = uiOperations.moveAction.bind(this)
    moveActions = uiOperations.moveActions.bind(this)
    deleteAction = uiOperations.deleteAction.bind(this)
    applyQueryToAction = uiOperations.applyQueryToAction.bind(this)
    applyUserFilters = uiOperations.applyUserFilters.bind(this)

    // Methods used to populate/maintain the actionsList
    createActionsList = populateActionList.createActionsList.bind(this)
    addActionToList = populateActionList.addActionToList.bind(this)
    validateRunbook = populateActionList.validateRunbook.bind(this)
    findLastSibling = populateActionList.findLastSibling.bind(this)
    indexAction = populateActionList.indexAction.bind(this)
    createNewAction = populateActionList.createNewAction.bind(this)
    addNewAction = populateActionList.addNewAction.bind(this)
    refreshActionFromDevice = populateActionList.refreshActionFromDevice.bind(
        this
    )
    processedChildrenSuccessfully = populateActionList.processedChildrenSuccessfully.bind(
        this
    )
    handleInvalidChildrenMessage = populateActionList.handleInvalidChildrenMessage.bind(
        this
    )
    checkChildrenLoops = populateActionList.checkChildrenLoops.bind(this)
    processChildren = populateActionList.processChildren.bind(this)
    canProcessAction = populateActionList.canProcessAction.bind(this)
    processAction = populateActionList.processAction.bind(this)
    markActionForDeletion = populateActionList.markActionForDeletion.bind(this)
    deleteMarkedActions = populateActionList.deleteMarkedActions.bind(this)
    calculateMaxDepth = populateActionList.calculateMaxDepth.bind(this)
    calculateTopActionUid = populateActionList.calculateTopActionUid.bind(this)
    markActionInfoOnly = populateActionList.markActionInfoOnly.bind(this)
    mapDeviceToAction = populateActionList.mapDeviceToAction.bind(this)
    addSolutionWithChildren = populateActionList.addSolutionWithChildren.bind(
        this
    )
    addAvAction = populateActionList.addAvAction.bind(this)

    // disable sorting during refresh TODO delete if business approves
    // sortChildActions = populateActionList.sortChildActions.bind(this)

    markEmptySolutionsAsInfoOnly = populateActionList.markEmptySolutionsAsInfoOnly.bind(
        this
    )

    // Methods used to filter out actions from the list through filter parameters (eg: location=Hasselt)
    equalsOperation = actionFiltering.equalsOperation.bind(this)
    likeOperation = actionFiltering.likeOperation.bind(this)
    notEqualsOperation = actionFiltering.notEqualsOperation.bind(this)
    checkDeviceFilters = actionFiltering.checkDeviceFilters.bind(this)
    notLikeOperation = actionFiltering.notLikeOperation.bind(this)
    updateFilteredStateOnChildren = actionFiltering.updateFilteredStateOnChildren.bind(
        this
    )
    filterOutBusinessSolutionChildren = actionFiltering.filterOutBusinessSolutionChildren.bind(
        this
    )
    propagateFilteredStateUp = actionFiltering.propagateFilteredStateUp.bind(
        this
    )
    propagateFilteredStateDown = actionFiltering.propagateFilteredStateDown.bind(
        this
    )
    applyRunbookFilters = actionFiltering.applyRunbookFilters.bind(this)

    // Methods related to refreshing the CMDB data and all cmdb action data population
    getResolverGroupIds = cmdbOperations.getResolverGroupIds.bind(this)
    getResolverGroups = cmdbOperations.getResolverGroups.bind(this)
    parseDevice = cmdbOperations.parseDevice.bind(this)
    extractDevices = cmdbOperations.extractDevices.bind(this)
    extractCiToUid = cmdbOperations.extractCiToUid.bind(this)
    extractCurrentAvZones = cmdbOperations.extractCurrentAvZones.bind(this)
    diffActionsWithDevices = cmdbOperations.diffActionsWithDevices.bind(this)
    mergeDevices = cmdbOperations.mergeDevices.bind(this)
    mergeNewSolutions = cmdbOperations.mergeNewSolutions.bind(this)
    refreshTriggerSolution = cmdbOperations.refreshTriggerSolution.bind(this)
    refreshTopSolution = cmdbOperations.refreshTopSolution.bind(this)
    refreshRegularSolution = cmdbOperations.refreshRegularSolution.bind(this)
    refreshVirpSolution = cmdbOperations.refreshVirpSolution.bind(this)
    getActionType = cmdbOperations.getActionType.bind(this)
    refreshActionFromCmdb = cmdbOperations.refreshActionFromCmdb.bind(this)
    getTopmostCmdbAction = cmdbOperations.getTopmostCmdbAction.bind(this)
    refreshSolutionsFromCmdb = cmdbOperations.refreshSolutionsFromCmdb.bind(
        this
    )
    parseTopSolution = cmdbOperations.parseTopSolution.bind(this)
    postProcessCiLinks = cmdbOperations.postProcessCiLinks.bind(this)
    processDrSolutions = cmdbOperations.processDrSolutions.bind(this)
    processCmdbData = cmdbOperations.processCmdbData.bind(this)
    getOrphanedUids = cmdbOperations.getOrphanedUids.bind(this)
    getState = cmdbOperations.getState.bind(this)
    isSolution = cmdbOperations.isSolution.bind(this)
    isVirp = cmdbOperations.isVirp.bind(this)
    isVirpPg = cmdbOperations.isVirpPg.bind(this)
    formatVirpName = cmdbOperations.formatVirpName.bind(this)
    extractVirpPg = cmdbOperations.extractVirpPg.bind(this)
    extractAvZones = cmdbOperations.extractAvZones.bind(this)
    newExtractAvZones = cmdbOperations.newExtractAvZones.bind(this)

    // Validation operations
    validateActionLinks = validationOperations.validateActionLinks.bind(this)
}
